import React, { KeyboardEvent, memo, useCallback, useMemo, useState } from 'react';
import { Button, Chip, Radio, Typography } from '@mui/material';
import styled from 'styled-components';

import AddFieldButton from 'components/AddFieldButton';
import ControlledTextField from 'components/ControlledTextField';
import DeleteIconButton from 'components/DeleteIconButton';
import DefaultOptionChooser from 'components/DefaultOptionChooser';
import OptionalMandatoryChooser from 'components/OptionalMandatoryChooser';
import UpDown from 'components/UpDown';
import { Action } from 'state';
import Choice from 'types/Choice';
import Field, { getNestedFieldsByChoice } from 'types/Field';
import FieldType from 'types/FieldType';
import Section from 'types/Section';
import NestedSelectFieldForm from './NestedSelectFieldForm';

const StyledRadio = styled(Radio)`
  padding-bottom: 0;

  &.MuiRadio-root {
    padding-bottom: 0.2rem;
  }
`;

const StyledChoiceRow = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledDeleteIconButton = styled(DeleteIconButton)`
  &.MuiIconButton-root {
    margin-right: 0.5rem;
    padding: 0.2rem;
  }
`;

const StyledDefaultOptionChooser = styled(DefaultOptionChooser)`
  &.MuiIconButton-root {
    padding: 0.2rem;
  }
`;

const StyledTextFieldLabel = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSingleSelectFieldForm = styled.div`
  & > *:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const StyledUpDown = styled(UpDown)`
  margin-right: 1rem;
`;

interface SingleSelectFieldFormProps {
  field: Field;
  section: Section;
  nestable?: boolean;
  showHeader?: boolean;
  dispatch: (action: Action) => void;
}

const SingleSelectFieldForm: React.FC<SingleSelectFieldFormProps> = ({
  section,
  field,
  nestable = true,
  showHeader = true,
  dispatch,
}) => {
  const [autoFocus, setAutoFocus] = useState(false);

  const handleOnChangeChoice = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, choice: Choice) => {
      const { value: label } = e.target;
      dispatch({ type: 'set-choice-label', section, field, choice, label });
    },
    [dispatch, section, field],
  );

  const handleOnChangeLabel = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const label = e.target.value;
      dispatch({ type: 'set-field-label', sectionUuid: section.uuid, field, label });
    },
    [dispatch, section, field],
  );

  const handleOnChangeOptionalMandatory = (required: boolean) => {
    dispatch({ type: 'set-field-required', sectionUuid: section.uuid, field, required });
  };

  const handleOnClickAddChoice = useCallback(() => {
    setAutoFocus(true);
    dispatch({ type: 'add-choice', section, field });
  }, [dispatch, section, field, setAutoFocus]);

  const handleOnKeyPressChoice = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleOnClickAddChoice();
      }
    },
    [handleOnClickAddChoice],
  );

  const handleOnClickAddNestedField = useCallback(
    (fieldType: FieldType, choice: Choice) => {
      dispatch({ type: 'add-nested-field', section, field, choice, fieldType });
    },
    [dispatch, section, field],
  );

  const handleOnClickDefaultOption = useCallback(
    (choice: Choice, isDefaultOption: boolean) => {
      dispatch({
        type: 'set-field-default-value',
        sectionUuid: section.uuid,
        field,
        defaultValue: choice.uuid,
        isDefaultOption,
      });
    },
    [dispatch, section, field],
  );

  const handleOnClickRemoveChoice = useCallback(
    (choice: Choice) => {
      dispatch({ type: 'remove-choice', section, field, choice });
    },
    [dispatch, section, field],
  );

  const handleOnClickRemoveField = useCallback(() => {
    dispatch({ type: 'remove-field', sectionUuid: section.uuid, field });
  }, [dispatch, section, field]);

  const handleOnMoveUp = useCallback(() => {
    dispatch({ type: 'move-field-up', sectionUuid: section.uuid, field });
  }, [dispatch, section, field]);

  const handleOnMoveDown = useCallback(() => {
    dispatch({ type: 'move-field-down', sectionUuid: section.uuid, field });
  }, [dispatch, section, field]);

  const nestedFieldsByChoice = useMemo(() => {
    return getNestedFieldsByChoice(field, section.exam_fields);
  }, [field, section.exam_fields]);

  const renderNestedFieldRow = (choice: Choice): React.ReactNode => {
    const nestedField = nestedFieldsByChoice[choice.uuid];

    if (!nestedField) return null;

    return <NestedSelectFieldForm section={section} field={nestedField} dispatch={dispatch} />;
  };

  return (
    <StyledSingleSelectFieldForm>
      {showHeader && (
        <StyledTextFieldLabel>
          <StyledUpDown onUp={handleOnMoveUp} onDown={handleOnMoveDown} />
          <ControlledTextField
            label="Field Label"
            size="small"
            fullWidth
            value={field.label}
            onChange={handleOnChangeLabel}
          />
          <OptionalMandatoryChooser required={field.is_required} onChange={handleOnChangeOptionalMandatory} />
          <Chip label="Single Select" />
          <DeleteIconButton onClick={handleOnClickRemoveField} />
        </StyledTextFieldLabel>
      )}
      {showHeader && field.choices_json.length > 0 && <Typography variant="subtitle1">Choices</Typography>}
      {field.choices_json.map((choice) => (
        <div key={choice.uuid}>
          <StyledChoiceRow key={choice.uuid}>
            <StyledRadio disabled />
            <ControlledTextField
              autoFocus={autoFocus}
              label="Choice Label"
              size="small"
              fullWidth
              value={choice.label}
              onChange={(e) => handleOnChangeChoice(e, choice)}
              onKeyDown={handleOnKeyPressChoice}
            />
            <StyledDefaultOptionChooser
              defaultOption={field.default_value?.some((choiceUuid) => choiceUuid === choice.uuid)}
              onChange={(defaultOption) => handleOnClickDefaultOption(choice, defaultOption)}
            />
            <StyledDeleteIconButton onClick={() => handleOnClickRemoveChoice(choice)} />
            {nestable && !nestedFieldsByChoice[choice.uuid] && (
              <AddFieldButton onClick={(fieldType) => handleOnClickAddNestedField(fieldType, choice)} />
            )}
          </StyledChoiceRow>
          {renderNestedFieldRow(choice)}
        </div>
      ))}
      <Button variant="contained" onClick={handleOnClickAddChoice}>
        Add Choice
      </Button>
    </StyledSingleSelectFieldForm>
  );
};

export default memo(SingleSelectFieldForm);
